import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import BACKEND_URL from '../config';
import Cookies from 'js-cookie';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import '../styles/swagger-overrides.css';

interface APIKey {
    id: number;
    public_key: string;
    name: string;
    created_at: string;
}

interface NewKeyResponse {
    public_token: string;
    private_token: string;
}

// ---------------------------------------------------------------------------
// Code-block component with copy button
// ---------------------------------------------------------------------------

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'python' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="relative group">
            <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    onClick={handleCopy}
                    className="px-2 py-1 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                >
                    {copied ? 'Copied!' : 'Copy'}
                </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm overflow-x-auto leading-relaxed">
                <code>{code}</code>
            </pre>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Collapsible section
// ---------------------------------------------------------------------------

const CollapsibleSection: React.FC<{
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}> = ({ title, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
                <span className="font-medium text-gray-800">{title}</span>
                <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && <div className="px-4 py-4 space-y-3">{children}</div>}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Getting Started guide
// ---------------------------------------------------------------------------

const SNIPPETS = {
    install: `pip install requests`,

    auth: `import requests

BASE_URL = "https://api.thoughtful-oasis.com/vnog/external-api"

# Your API credentials (generated in the panel above)
HEADERS = {
    "X-Public-Key": "pk_live_abc123...",
    "X-Private-Key": "sk_live_xyz789...",
}

# Test authentication by listing situations
response = requests.get(f"{BASE_URL}/situations/", headers=HEADERS)
response.raise_for_status()

print(response.json())`,

    listSimulations: `import requests

BASE_URL = "https://api.thoughtful-oasis.com/vnog/external-api"
HEADERS = {
    "X-Public-Key": "pk_live_abc123...",
    "X-Private-Key": "sk_live_xyz789...",
}

# List all simulations
response = requests.get(f"{BASE_URL}/simulations/", headers=HEADERS)
simulations = response.json()

for sim in simulations:
    print(f"[{sim['id']}] {sim['status']} — {sim['created_at']}")`,

    retrieveOne: `# Retrieve a single simulation by ID
simulation_id = 101
response = requests.get(
    f"{BASE_URL}/simulations/{simulation_id}/",
    headers=HEADERS,
)
response.raise_for_status()

sim = response.json()
print(f"Simulation {sim['id']}")
print(f"  Status:              {sim['status']}")
print(f"  Address:             {sim['address']['street']} {sim['address']['house_number']}")
print(f"  Created:             {sim['created_at']}")
print(f"  Relevance threshold: {sim['relevance_threshold']}")
print(f"  Allowed phases:      {[p['slug'] for p in sim['allowed_phases']] or 'all'}")
print(f"  Allowed roles:       {[r['slug'] for r in sim['allowed_roles']] or 'all'}")`,

    filtering: `# Filter simulations by status
response = requests.get(
    f"{BASE_URL}/simulations/",
    headers=HEADERS,
    params={"status": "finished"},
)
finished = response.json()
print(f"Found {len(finished)} finished simulations")

# Filter characteristics by data type
response = requests.get(
    f"{BASE_URL}/characteristics/",
    headers=HEADERS,
    params={"data_type": "boolean", "enabled": "true"},
)
chars = response.json()
for c in chars:
    print(f"  {c['name']} ({c['data_type']})")

# Filter documentation by phase / role / minimum relevance
docs = requests.get(
    f"{BASE_URL}/documentation/",
    headers=HEADERS,
    params={"phase": 2, "role": 3, "min_relevance": 0.8},
).json()
print(f"Found {len(docs)} documents in the selected phase/role")`,

    phasesRoles: `# List the phase and role catalogs.
# These are used both to tag documentation items and to scope simulations.

phases = requests.get(f"{BASE_URL}/phases/", headers=HEADERS).json()
for p in phases:
    print(f"  phase #{p['id']:>3} {p['slug']:<20} — {p['description']}")

roles = requests.get(f"{BASE_URL}/roles/", headers=HEADERS).json()
for r in roles:
    print(f"  role  #{r['id']:>3} {r['slug']:<20} — {r['description']}")

# A simulation exposes its scoping configuration:
#   - relevance_threshold: minimum Documentation.relevance to be retrievable
#   - allowed_phases / allowed_roles: empty list means "all allowed"
sim = requests.get(f"{BASE_URL}/simulations/101/", headers=HEADERS).json()
print(f"relevance_threshold: {sim['relevance_threshold']}")
print(f"allowed phases: {[p['slug'] for p in sim['allowed_phases']] or 'ALL'}")
print(f"allowed roles:  {[r['slug'] for r in sim['allowed_roles']] or 'ALL'}")`,

    ordering: `# Order simulations by creation date (ascending)
response = requests.get(
    f"{BASE_URL}/simulations/",
    headers=HEADERS,
    params={"ordering": "created_at"},
)

# Order simulations by creation date (descending)
response = requests.get(
    f"{BASE_URL}/simulations/",
    headers=HEADERS,
    params={"ordering": "-created_at"},
)`,

    fullWorkflow: `import requests

BASE_URL = "https://api.thoughtful-oasis.com/vnog/external-api"
HEADERS = {
    "X-Public-Key": "pk_live_abc123...",
    "X-Private-Key": "sk_live_xyz789...",
}

# 1. Get all finished simulations
simulations = requests.get(
    f"{BASE_URL}/simulations/",
    headers=HEADERS,
    params={"status": "finished", "ordering": "-created_at"},
).json()

print(f"Found {len(simulations)} finished simulations\\n")

for sim in simulations[:5]:  # Process the 5 most recent
    sim_id = sim["id"]
    print(f"=== Simulation {sim_id} ===")
    print(f"  Address:    {sim['address']['street']} {sim['address']['house_number']}")
    print(f"  Threshold:  relevance >= {sim['relevance_threshold']}")
    print(f"  Phases:     {[p['slug'] for p in sim['allowed_phases']] or 'all'}")
    print(f"  Roles:      {[r['slug'] for r in sim['allowed_roles']] or 'all'}")

    # 2. Get characteristic estimations for this simulation
    estimations = requests.get(
        f"{BASE_URL}/characteristics-estimations/",
        headers=HEADERS,
        params={"simulation": sim_id},
    ).json()

    for est in estimations:
        est_id = est["id"]

        # 3. Get individual estimation values
        values = requests.get(
            f"{BASE_URL}/characteristic-estimations/",
            headers=HEADERS,
            params={"characteristics_estimation": est_id},
        ).json()

        print(f"  Estimation batch {est_id}: {len(values)} characteristics")
        for val in values[:3]:
            char_name = val.get("characteristic", {}).get("name", "?")
            print(f"    - {char_name}: {val.get('value')}")

    # 4. Get AI operator messages
    messages = requests.get(
        f"{BASE_URL}/ai-operator-messages/",
        headers=HEADERS,
        params={"simulation": sim_id},
    ).json()
    print(f"  AI operator messages: {len(messages)}")

    # 5. Get documentation retrieved during simulation, including
    #    each document's phase/role tags.
    docs = requests.get(
        f"{BASE_URL}/documentation-retrieved/",
        headers=HEADERS,
        params={"simulation": sim_id},
    ).json()
    print(f"  Docs retrieved: {len(docs)}")
    for d in docs[:3]:
        full = requests.get(
            f"{BASE_URL}/documentation/{d['documentation_id']}/",
            headers=HEADERS,
        ).json()
        phases = [p['slug'] for p in full.get('phases', [])]
        roles = [r['slug'] for r in full.get('roles', [])]
        print(f"    - {full['summary_name']}  phases={phases}  roles={roles}")
    print()`,

    errorHandling: `import requests

BASE_URL = "https://api.thoughtful-oasis.com/vnog/external-api"
HEADERS = {
    "X-Public-Key": "pk_live_abc123...",
    "X-Private-Key": "sk_live_xyz789...",
}

def api_get(endpoint: str, params: dict = None) -> dict | list:
    """Helper function with error handling for API requests."""
    url = f"{BASE_URL}/{endpoint.strip('/')}/"
    try:
        response = requests.get(url, headers=HEADERS, params=params, timeout=30)
        response.raise_for_status()
        return response.json()
    except requests.exceptions.HTTPError as e:
        if e.response.status_code == 401:
            raise SystemExit("Authentication failed — check your API keys.")
        elif e.response.status_code == 404:
            raise ValueError(f"Resource not found: {url}")
        else:
            raise RuntimeError(f"API error {e.response.status_code}: {e.response.text}")
    except requests.exceptions.ConnectionError:
        raise ConnectionError(f"Could not connect to {BASE_URL}")
    except requests.exceptions.Timeout:
        raise TimeoutError(f"Request to {url} timed out")

# Usage
simulations = api_get("simulations", params={"status": "finished"})
print(f"Got {len(simulations)} simulations")

try:
    sim = api_get("simulations/99999")
except ValueError as e:
    print(f"Expected: {e}")`,
};

const GettingStartedGuide: React.FC = () => {
    return (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h3 className="text-lg font-semibold mb-1">Getting Started</h3>
            <p className="text-sm text-gray-600 mb-5">
                Follow these steps to start consuming the external API from Python.
                All endpoints are <span className="font-medium">read-only</span> (GET requests only).
            </p>

            <div className="space-y-3">
                {/* 1. Install */}
                <CollapsibleSection title="1. Install dependencies" defaultOpen>
                    <p className="text-sm text-gray-600">
                        The only dependency you need is the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">requests</code> library:
                    </p>
                    <CodeBlock code={SNIPPETS.install} language="bash" />
                </CollapsibleSection>

                {/* 2. Authentication */}
                <CollapsibleSection title="2. Authenticate with API keys" defaultOpen>
                    <p className="text-sm text-gray-600">
                        Every request must include your <strong>X-Public-Key</strong> and <strong>X-Private-Key</strong> headers.
                        Generate a key pair using the panel above — the private key is shown only once, so save it immediately.
                    </p>
                    <CodeBlock code={SNIPPETS.auth} />
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-800">
                            <strong>Tip:</strong> Store your keys in environment variables or a secrets manager — never hard-code them in source files you commit.
                        </p>
                    </div>
                </CollapsibleSection>

                {/* 3. Listing resources */}
                <CollapsibleSection title="3. List resources">
                    <p className="text-sm text-gray-600">
                        Every resource supports a <strong>list</strong> endpoint that returns all records.
                        For example, to list simulations:
                    </p>
                    <CodeBlock code={SNIPPETS.listSimulations} />
                </CollapsibleSection>

                {/* 4. Retrieve a single resource */}
                <CollapsibleSection title="4. Retrieve a single resource">
                    <p className="text-sm text-gray-600">
                        Append the resource ID to the URL to get a single record with full details:
                    </p>
                    <CodeBlock code={SNIPPETS.retrieveOne} />
                </CollapsibleSection>

                {/* 5. Filtering */}
                <CollapsibleSection title="5. Filter results">
                    <p className="text-sm text-gray-600">
                        Most endpoints accept query parameters to filter results.
                        Check the API reference below for each endpoint's available filters.
                    </p>
                    <CodeBlock code={SNIPPETS.filtering} />
                </CollapsibleSection>

                {/* 6. Ordering */}
                <CollapsibleSection title="6. Order results">
                    <p className="text-sm text-gray-600">
                        Use the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">ordering</code> query
                        parameter to sort results. Prefix with <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">-</code> for
                        descending order.
                    </p>
                    <CodeBlock code={SNIPPETS.ordering} />
                </CollapsibleSection>

                {/* 7. Phases & roles */}
                <CollapsibleSection title="7. Phases, roles & documentation scoping">
                    <p className="text-sm text-gray-600">
                        Documentation items are tagged with <strong>phases</strong> and <strong>roles</strong>,
                        and each simulation may restrict retrieval to a subset of them and to a minimum
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">relevance</code> score.
                        Use the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/phases/</code> and
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/roles/</code> endpoints to
                        list the catalogs, and the
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">phase</code>,
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">role</code>, and
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">min_relevance</code> query
                        parameters on <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/documentation/</code> to
                        narrow the list. An empty
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">allowed_phases</code> /
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">allowed_roles</code> list on a
                        simulation means &quot;all allowed&quot;.
                    </p>
                    <CodeBlock code={SNIPPETS.phasesRoles} />
                </CollapsibleSection>

                {/* 8. Error handling */}
                <CollapsibleSection title="8. Error handling">
                    <p className="text-sm text-gray-600">
                        Wrap your calls in proper error handling. The API returns standard HTTP status codes:
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs my-2">
                        <div className="bg-green-50 border border-green-200 rounded px-3 py-2">
                            <span className="font-mono font-bold text-green-700">200</span>
                            <span className="text-gray-600 ml-2">Success</span>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                            <span className="font-mono font-bold text-yellow-700">401</span>
                            <span className="text-gray-600 ml-2">Invalid or missing API keys</span>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded px-3 py-2">
                            <span className="font-mono font-bold text-red-700">404</span>
                            <span className="text-gray-600 ml-2">Resource not found</span>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded px-3 py-2">
                            <span className="font-mono font-bold text-red-700">500</span>
                            <span className="text-gray-600 ml-2">Server error</span>
                        </div>
                    </div>
                    <CodeBlock code={SNIPPETS.errorHandling} />
                </CollapsibleSection>

                {/* 9. Full workflow */}
                <CollapsibleSection title="9. Full example — export simulation data">
                    <p className="text-sm text-gray-600">
                        This example ties it all together: fetch finished simulations, then pull their
                        characteristic estimations, AI operator messages, and retrieved documentation.
                    </p>
                    <CodeBlock code={SNIPPETS.fullWorkflow} />
                </CollapsibleSection>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ExternalAPI: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newKey, setNewKey] = useState<NewKeyResponse | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const fetchKeys = useCallback(async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/api-keys/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setApiKeys(data);
            } else {
                toast.error('Failed to fetch API keys');
            }
        } catch (error) {
            toast.error('Error fetching API keys');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchKeys();
    }, [fetchKeys]);

    const handleCreateKey = async () => {
        setCreating(true);
        setNewKey(null);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/api-keys/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
            });
            if (response.ok) {
                const data: NewKeyResponse = await response.json();
                setNewKey(data);
                toast.success('API key created successfully');
                fetchKeys();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to create API key');
            }
        } catch (error) {
            toast.error('Error creating API key');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteKey = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this API key? Any integrations using it will stop working.')) {
            return;
        }
        setDeletingId(id);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/api-keys/${id}/delete/`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
            });
            if (response.ok || response.status === 204) {
                toast.success('API key deleted');
                setApiKeys((prev) => prev.filter((k) => k.id !== id));
                if (newKey) {
                    // Clear the displayed key if we can't be sure it's still valid
                    setNewKey(null);
                }
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to delete API key');
            }
        } catch (error) {
            toast.error('Error deleting API key');
        } finally {
            setDeletingId(null);
        }
    };

    const handleCycleKey = async () => {
        if (apiKeys.length > 0 && !window.confirm('This will create a new key. You can delete old keys afterwards. Continue?')) {
            return;
        }
        await handleCreateKey();
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success(`${label} copied to clipboard`);
        }).catch(() => {
            toast.error('Failed to copy to clipboard');
        });
    };

    return (
        <div>
            <h2 className="text-2xl font-semibold mb-4">External API Keys</h2>

            {/* Create / Cycle Key */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-medium">Generate New API Key</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Create a new public/private key pair for external API access.
                        </p>
                    </div>
                    <button
                        onClick={handleCycleKey}
                        disabled={creating}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {creating ? 'Generating...' : 'Generate New Key'}
                    </button>
                </div>

                {/* Newly created key display */}
                {newKey && (
                    <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4 mt-4">
                        <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-yellow-800 mb-3">
                                    Save these credentials now. The private key will not be shown again.
                                </p>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Public Key</label>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 bg-white px-3 py-2 rounded border border-gray-300 text-sm font-mono break-all">
                                                {newKey.public_token}
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(newKey.public_token, 'Public key')}
                                                className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 flex-shrink-0"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Private Key</label>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 bg-white px-3 py-2 rounded border border-gray-300 text-sm font-mono break-all">
                                                {newKey.private_token}
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(newKey.private_token, 'Private key')}
                                                className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 flex-shrink-0"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Existing Keys Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">Existing API Keys</h3>
                </div>
                {loading ? (
                    <div className="px-6 py-8 text-center text-gray-500">Loading API keys...</div>
                ) : apiKeys.length === 0 ? (
                    <div className="px-6 py-8 text-center text-gray-500">
                        No API keys yet. Generate one above to get started.
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Public Key
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {apiKeys.map((key) => (
                                <tr key={key.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {key.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center gap-2">
                                            <code className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                                {key.public_key.slice(0, 12)}...{key.public_key.slice(-8)}
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(key.public_key, 'Public key')}
                                                className="text-blue-600 hover:text-blue-800 text-xs"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(key.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => handleDeleteKey(key.id)}
                                            disabled={deletingId === key.id}
                                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md text-sm disabled:opacity-50"
                                        >
                                            {deletingId === key.id ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Getting Started Guide */}
            <GettingStartedGuide />

            {/* API Documentation (Swagger UI) */}
            <div className="bg-white rounded-lg shadow mt-6 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">API Documentation</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Browse all available read-only endpoints. Use the filter bar to search for specific resources.
                    </p>
                </div>
                <div className="vnog-swagger">
                    <SwaggerUI
                        url={`${BACKEND_URL}/vnog/external-api/schema/`}
                        withCredentials
                        docExpansion="list"
                        defaultModelsExpandDepth={1}
                        tryItOutEnabled
                        deepLinking
                        filter
                        persistAuthorization
                    />
                </div>
            </div>
        </div>
    );
};

export default ExternalAPI;
